import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button, Paper } from '@c2fo/liquidity';
import type { Division, User } from '../UserDetail/types';
import { useSimulatorTemplates } from './hooks/useSimulatorTemplates';
import { useExchangeRates } from './hooks/useExchangeRates';
import { useSrmDataQuery } from './hooks/useSrmDataQuery';
import { useOfferSimulator } from './hooks/useOfferSimulator';
import { OfferSimulatorControls } from './components/OfferSimulatorControls';
import { OfferSimulatorTable } from './components/OfferSimulatorTable';
import { TemplateRenderer } from './components/TemplateRenderer';
import { renderTemplate, buildTemplateHelpers, getSimulationHelpers } from './utils/templateRenderer';
import type { DivisionWithPotentialBid } from './utils/templateRenderer';

interface OfferSimulatorProps {
  divisions: Division[];
  user?: User | null;
}

export const OfferSimulator: React.FC<OfferSimulatorProps> = ({ divisions, user }) => {
  const { templates, selectedTemplate, selectTemplate } = useSimulatorTemplates();
  const [selectedCurrency, setSelectedCurrency] = useState<string>('USD');
  const [error, setError] = useState<string | null>(null);
  const { exchangeRates } = useExchangeRates(selectedCurrency);
  const { data: srmData } = useSrmDataQuery(user?.emailAddress);
  const copyTimeoutRef = useRef<number | null>(null);

  const [displayOptions, setDisplayOptions] = useState({
    showTable: true,
    showOfferColumn: true,
    showDiscountPercentColumn: true,
    showDiscountDollarColumn: true,
    showNet: true,
    showVendorId: true,
    showSummations: true,
    lastParticipationDate: true,
  });

  const [renderedTemplate, setRenderedTemplate] = useState<{
    intro: string;
    outro: string;
    table: string;
  } | null>(null);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const [clipboardError, setClipboardError] = useState<string | null>(null);

  const {
    divisionsWithBids,
    globalPotentialBid,
    setGlobalPotentialBid,
    simulateOffersLoading,
    offerSuggestionsLoading,
    simulationIsDirty,
    showMissingOfferSuggestionsWarning,
    hasCheckableRows,
    enabledDivisions,
    allDivisionsEnabled,
    divisionsWithBidsReady,
    handleSetAllPotentialBids,
    handleGetOfferSuggestions,
    handleSimulateOffers,
    handleDivisionBidChange,
    handleDivisionEnabledChange,
    handleToggleAll,
  } = useOfferSimulator({ divisions });

  const filteredDivisions = useMemo(() => {
    return divisionsWithBids.sort((a, b) => b.eligibleInvoiceAmountUsd - a.eligibleInvoiceAmountUsd);
  }, [divisionsWithBids]);

  const enabledDivisionsWithSimulations = useMemo(() => {
    return enabledDivisions.filter((d) => d.simulation);
  }, [enabledDivisions]);

  const availableCurrencies = useMemo(() => {
    const currencies = Array.from(new Set(enabledDivisionsWithSimulations.map((d) => d.currency).filter(Boolean)));
    return currencies.length > 0 ? currencies : ['USD'];
  }, [enabledDivisionsWithSimulations]);

  useEffect(() => {
    if (selectedCurrency && !availableCurrencies.includes(selectedCurrency)) {
      setSelectedCurrency(availableCurrencies.includes('USD') ? 'USD' : (availableCurrencies[0] ?? 'USD'));
    }
  }, [availableCurrencies, selectedCurrency]);

  const escapeHtml = useCallback((text: string | null | undefined): string => {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }, []);

  const formatCurrency = useCallback((amount: number, currency: string): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }, []);

  const formatPercent = useCallback((value: number, decimals = 2): string => {
    return `${value.toFixed(decimals)}%`;
  }, []);

  const generateTableHtml = useCallback(
    (
      divisions: DivisionWithPotentialBid[],
      options: typeof displayOptions,
      currency: string,
      exchangeRates: Record<string, number> | null,
    ): string => {
      const convertAmount = (amount: number, fromCurrency: string): number => {
        if (!exchangeRates || fromCurrency === currency) return amount;
        const fromRate = exchangeRates[fromCurrency] ?? 1;
        const toRate = exchangeRates[currency] ?? 1;
        return amount * (fromRate / toRate);
      };

      const headers: string[] = ['Market', 'Division'];
      if (options.showVendorId) headers.push('Vendor Id');
      if (options.showOfferColumn) headers.push('Offer');
      headers.push('Invoices');
      headers.push('Days Paid Early');
      if (options.showDiscountDollarColumn) headers.push('Discount');
      if (options.showDiscountPercentColumn) headers.push('Discount %');
      if (options.showNet) headers.push('Net Received');
      headers.push('New Pay Date');
      if (options.lastParticipationDate) headers.push('Last Participation Date');

      let html = '<table style="border-collapse: collapse; width: 100%; border: 1px solid #d1d5db;">';
      html += '<thead><tr>';
      headers.forEach((header) => {
        html += `<th style="border: 1px solid #d1d5db; padding: 8px; text-align: left; background-color: #f9fafb;">${escapeHtml(header)}</th>`;
      });
      html += '</tr></thead><tbody>';

      divisions.forEach((division) => {
        if (!division.simulation) return;

        const divCurrency = division.currency || 'USD';
        const discountedAmount = convertAmount(division.simulation.discountedAmount, divCurrency);
        const discount = convertAmount(division.simulation.discount, divCurrency);

        html += '<tr>';
        html += `<td style="border: 1px solid #d1d5db; padding: 8px;">${escapeHtml(division.makerOrganizationName || '')}</td>`;
        html += `<td style="border: 1px solid #d1d5db; padding: 8px;">${escapeHtml(division.normalizedName || '')}</td>`;

        if (options.showVendorId) {
          html += `<td style="border: 1px solid #d1d5db; padding: 8px;">${escapeHtml(division.buyerSupplierId || '')}</td>`;
        }

        if (options.showOfferColumn) {
          html += `<td style="border: 1px solid #d1d5db; padding: 8px;">${formatPercent(division.simulation.maxBid * 100)}</td>`;
        }

        html += `<td style="border: 1px solid #d1d5db; padding: 8px;">${formatCurrency(
          convertAmount(division.eligibleInvoiceAmount, divCurrency),
          currency,
        )}</td>`;
        html += `<td style="border: 1px solid #d1d5db; padding: 8px;">${division.eligibleDpeWeightedAvg || 0}</td>`;

        if (options.showDiscountDollarColumn) {
          html += `<td style="border: 1px solid #d1d5db; padding: 8px;">${formatCurrency(discount, currency)}</td>`;
        }

        if (options.showDiscountPercentColumn) {
          html += `<td style="border: 1px solid #d1d5db; padding: 8px;">${formatPercent(division.simulation.discountPercent * 100)}</td>`;
        }

        if (options.showNet) {
          html += `<td style="border: 1px solid #d1d5db; padding: 8px;">${formatCurrency(discountedAmount, currency)}</td>`;
        }

        html += `<td style="border: 1px solid #d1d5db; padding: 8px;">${
          division.payDate ? division.payDate.split('T')[0] : ''
        }</td>`;
        if (options.lastParticipationDate) {
          html += `<td style="border: 1px solid #d1d5db; padding: 8px;">${
            division.lastMakerMarketClearDate ? division.lastMakerMarketClearDate.split('T')[0] : ''
          }</td>`;
        }

        html += '</tr>';
      });

      if (options.showSummations) {
        const totalInvoices = divisions.reduce((sum, d) => {
          if (!d.simulation) return sum;
          const divCurrency = d.currency || 'USD';
          return sum + convertAmount(d.simulation.discountedAmount + d.simulation.discount, divCurrency);
        }, 0);
        const totalDiscount = divisions.reduce((sum, d) => {
          if (!d.simulation) return sum;
          const divCurrency = d.currency || 'USD';
          return sum + convertAmount(d.simulation.discount, divCurrency);
        }, 0);
        const totalNet = divisions.reduce((sum, d) => {
          if (!d.simulation) return sum;
          const divCurrency = d.currency || 'USD';
          return sum + convertAmount(d.simulation.discountedAmount, divCurrency);
        }, 0);

        const totalsColspan = 2 + (options.showVendorId ? 1 : 0) + (options.showOfferColumn ? 1 : 0);

        html += '<tr style="font-weight: bold; background-color: #f5f5f5; border-top: 2px solid #9ca3af;">';
        html += `<td colspan="${totalsColspan}" style="border: 1px solid #d1d5db; padding: 8px;">TOTALS</td>`;
        html += `<td style="border: 1px solid #d1d5db; padding: 8px;">${formatCurrency(totalInvoices, currency)}</td>`;
        html += '<td style="border: 1px solid #d1d5db; padding: 8px;"></td>';

        if (options.showDiscountDollarColumn) {
          html += `<td style="border: 1px solid #d1d5db; padding: 8px;">${formatCurrency(totalDiscount, currency)}</td>`;
        }

        if (options.showDiscountPercentColumn) {
          html += '<td style="border: 1px solid #d1d5db; padding: 8px;"></td>';
        }

        if (options.showNet) {
          html += `<td style="border: 1px solid #d1d5db; padding: 8px;">${formatCurrency(totalNet, currency)}</td>`;
        }

        html += '<td style="border: 1px solid #d1d5db; padding: 8px;"></td>';

        if (options.lastParticipationDate) {
          html += '<td style="border: 1px solid #d1d5db; padding: 8px;"></td>';
        }

        html += '</tr>';
      }

      html += '</tbody></table>';
      return html;
    },
    [escapeHtml, formatCurrency, formatPercent],
  );

  useEffect(() => {
    if (!selectedTemplate || enabledDivisionsWithSimulations.length === 0) {
      setRenderedTemplate(null);
      return;
    }

    const templateParts = selectedTemplate.template.split('{{!table}}');
    const introTemplate = templateParts[0] ?? '';
    const outroTemplate = templateParts[1] ?? '';

    const simulationHelpers = enabledDivisionsWithSimulations.map((d) =>
      getSimulationHelpers(d, selectedCurrency, exchangeRates),
    );
    const helpers = buildTemplateHelpers(enabledDivisionsWithSimulations, selectedCurrency, exchangeRates);

    const intro = renderTemplate(user ?? null, introTemplate, srmData ?? null, simulationHelpers, helpers);
    const outro = renderTemplate(user ?? null, outroTemplate, srmData ?? null, simulationHelpers, helpers);

    let tableHtml = '';
    if (displayOptions.showTable) {
      tableHtml = generateTableHtml(enabledDivisionsWithSimulations, displayOptions, selectedCurrency, exchangeRates);
    }

    setRenderedTemplate({ intro, outro, table: tableHtml });
  }, [
    selectedTemplate,
    enabledDivisionsWithSimulations,
    user,
    srmData,
    selectedCurrency,
    displayOptions,
    generateTableHtml,
    exchangeRates,
  ]);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const handleCopyToClipboard = useCallback(async () => {
    if (!renderedTemplate) return;

    const stripHtml = (html: string): string => {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    };

    const introText = stripHtml(renderedTemplate.intro);
    const tableText = displayOptions.showTable ? stripHtml(renderedTemplate.table) : '';
    const outroText = stripHtml(renderedTemplate.outro);

    const fullText = [introText, tableText, outroText].filter(Boolean).join('\n\n');

    try {
      setClipboardError(null);
      await navigator.clipboard.writeText(fullText);
      setCopiedToClipboard(true);

      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }

      copyTimeoutRef.current = window.setTimeout(() => {
        setCopiedToClipboard(false);
        copyTimeoutRef.current = null;
      }, 2000);
    } catch (err) {
      setClipboardError('Failed to copy to clipboard. Please try selecting and copying the text manually.');
      setTimeout(() => setClipboardError(null), 5000);
    }
  }, [renderedTemplate, displayOptions.showTable]);

  const totals = useMemo(() => {
    const simulatedDivisions = filteredDivisions.filter((d) => d.simulation);
    if (simulatedDivisions.length === 0) {
      return {
        blendedRate: '-',
        discount: '-',
        discountPercent: '-',
        discountedAmount: '-',
      };
    }

    const totalDiscount = simulatedDivisions.reduce((sum, d) => sum + (d.simulation?.discount || 0), 0);
    const totalDiscountedAmount = simulatedDivisions.reduce((sum, d) => sum + (d.simulation?.discountedAmount || 0), 0);
    const totalEligible = simulatedDivisions.reduce((sum, d) => sum + d.eligibleInvoiceAmount, 0);
    const avgDiscountPercent = totalEligible > 0 ? (totalDiscount / totalEligible) * 100 : 0;
    const avgRate =
      simulatedDivisions.reduce((sum, d) => sum + (d.simulation?.maxBid || 0), 0) / simulatedDivisions.length;

    return {
      blendedRate: `${avgRate.toFixed(2)}%`,
      discount: `$${totalDiscount.toFixed(2)}`,
      discountPercent: `${avgDiscountPercent.toFixed(2)}%`,
      discountedAmount: `$${totalDiscountedAmount.toFixed(2)}`,
    };
  }, [filteredDivisions]);

  return (
    <Paper elevation={1} square className="p-6">
      <h3 className="text-lg font-semibold mb-4">Supplier Offer Simulator</h3>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 mb-6">{error}</div>}

      <OfferSimulatorControls
        globalPotentialBid={globalPotentialBid}
        onGlobalPotentialBidChange={setGlobalPotentialBid}
        onSetAllPotentialBids={handleSetAllPotentialBids}
        onGetOfferSuggestions={async () => {
          try {
            setError(null);
            await handleGetOfferSuggestions();
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load offer suggestions');
          }
        }}
        simulateOffersLoading={simulateOffersLoading}
        offerSuggestionsLoading={offerSuggestionsLoading}
        divisionsWithBids={divisionsWithBids}
        hasCheckableRows={hasCheckableRows}
        showMissingOfferSuggestionsWarning={showMissingOfferSuggestionsWarning}
      />

      <OfferSimulatorTable
        filteredDivisions={filteredDivisions}
        allDivisionsEnabled={allDivisionsEnabled}
        simulateOffersLoading={simulateOffersLoading}
        handleToggleAll={handleToggleAll}
        handleDivisionEnabledChange={handleDivisionEnabledChange}
        handleDivisionBidChange={handleDivisionBidChange}
        totals={totals}
      />

      <div className="flex justify-center mb-6">
        <Button
          variant={divisionsWithBidsReady.length > 0 ? 'primary' : 'secondary'}
          size="lg"
          onClick={async () => {
            try {
              setError(null);
              await handleSimulateOffers();
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Failed to simulate offers');
            }
          }}
          disabled={simulateOffersLoading || divisionsWithBidsReady.length === 0}
          loading={simulateOffersLoading}
        >
          Simulate {divisionsWithBidsReady.length} Offers
        </Button>
      </div>

      {filteredDivisions.some((d) => d.simulation) ? (
        <>
          <hr className="my-6 border-stroke" />

          {simulationIsDirty && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-800">
              These emails are from a previous simulation.
            </div>
          )}

          <TemplateRenderer
            templates={templates}
            selectedTemplate={selectedTemplate}
            selectTemplate={selectTemplate}
            selectedCurrency={selectedCurrency}
            setSelectedCurrency={setSelectedCurrency}
            availableCurrencies={availableCurrencies}
            displayOptions={displayOptions}
            setDisplayOptions={setDisplayOptions}
            renderedTemplate={renderedTemplate}
            copiedToClipboard={copiedToClipboard}
            clipboardError={clipboardError}
            onCopyToClipboard={handleCopyToClipboard}
          />
        </>
      ) : (
        <div className="mt-6">
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded text-gray-600">
            No emails to show. Run a simulation to generate email content.
          </div>
        </div>
      )}
    </Paper>
  );
};
